import { BrowserFileUpload, type UploadTaskFile } from '@/platform/upload/BrowserFileUpload'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { useAuthStore } from '@/features/auth'
import { useWorkspaceStore } from '@/store/workspace'
import {
  addGameAttrs,
  addUpdateLog,
  checkBlackWords,
  checkIfNeedTag,
  checkProjectPublishAuth,
  confirmPublishFilePutSign,
  getPublishGindex,
  getPublishVersion,
  isTagMissing,
  publishProjectRequest,
  submitPublishResult,
  updateProjectName,
} from '@/publish/api'
import { uploadPublishedCover, type PublishCoverUploadStatus } from '@/publish/services/uploadPublishCover'
import {
  downloadPublishedProjectFile,
  getPublishedCyGameListPage,
  getPublishedProjectVersions,
  resolvePublishedGindexByGuid,
  type PublishedProjectVersionItem,
} from '@/features/published-project/api'
import { buildPublishArtifacts, createUploadFile } from '@/publish/services/buildPublishArtifacts'
import { submitGameTags } from '@/publish/services/tagSetup'
import { projectOssSaveService } from '@/features/project-cloud/ossSave'
import { buildPublishedGamePageUrl } from '@/publish/gamePageUrl'
import {
  hasInvalidProjectNameChar,
  isProjectNameLengthValid,
  PROJECT_NAME_INVALID_CHAR_MESSAGE,
  PROJECT_NAME_LIMIT_MESSAGE,
} from '@/utils/projectNameLimit'
import type MainProject from '@/model/MainProject'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import type {
  PublishArtifacts,
  PublishCheckItem,
  PublishContext,
  PublishFormModel,
  PublishProgressItem,
  PublishResult,
  PublishRawTagList,
} from '@/publish/types'
import { APP_NAME, APP_VERSION } from '@/config/appVersion'

type ProgressStatus = PublishProgressItem['status']
const PUBLISH_GET_GAME_PATH_RETRY_COUNT = 10
const PUBLISH_GET_GAME_PATH_RETRY_DELAY_MS = 1000
const MAP_BIN_PUTSIGN_FILE_NAME = 'bin/Map.bin'
const PUBLISHED_PROJECT_OWNER_CHECK_PAGE_LIMIT = 50
type ProjectVersionCarrier = {
  projectConstant: { gameVersion: number }
  projectHeader: { Version: number }
}

type PublishResultStats = {
  picCount: number
  faceCount: number
  modCount: number
  bgmCount: number
  bgsCount: number
  seCount: number
  voiceCount: number
}

export type PublishFlowHooks = {
  onStep?: (key: string, status: ProgressStatus, detail?: string) => void
  onUploadProgress?: (fileName: string, percent: number, status: string) => void
  onRequest?: (status: ProgressStatus, detail: string) => void
}

export type PublishCheckOptions = {
  stagedTagSelection?: PublishRawTagList | null
  tagSelectionSubmitted?: boolean
  skipGateChecks?: boolean
}

function makeCheck(
  key: string,
  level: PublishCheckItem['level'],
  title: string,
  message: string,
): PublishCheckItem {
  return { key, level, title, message }
}

function markStep(hooks: PublishFlowHooks | undefined, key: string, status: ProgressStatus, detail?: string) {
  hooks?.onStep?.(key, status, detail)
}

function markRequest(hooks: PublishFlowHooks | undefined, status: ProgressStatus, detail: string) {
  hooks?.onRequest?.(status, detail)
}

function waitPublishDelay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

function formatPublishFixedTime(): string {
  const date = new Date()
  return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} 00:00:00`
}

function normalizePublishVersionNumber(value: string): number {
  const version = Number.parseInt(String(value || '').trim(), 10)
  return Number.isFinite(version) && version > 0 ? version : 0
}

function resolveLatestPublishedVersion(
  versions: PublishedProjectVersionItem[],
): { version: number; gindex: string | null } {
  let latestVersion = 0
  let latestGindex: string | null = null
  for (const item of versions) {
    const version = normalizePublishVersionNumber(item.version)
    if (version > latestVersion) {
      latestVersion = version
      latestGindex = item.gindex.trim() || null
    }
  }
  return { version: latestVersion, gindex: latestGindex }
}

function getLocalProjectVersion(project: ProjectVersionCarrier): number {
  const value = Number(project.projectHeader?.Version || project.projectConstant?.gameVersion || 0)
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0
}

function writeProjectGameVersion(project: ProjectVersionCarrier, version: string): void {
  const value = Number.parseInt(version, 10)
  if (!Number.isFinite(value) || value <= 0) return
  project.projectConstant.gameVersion = value
  project.projectHeader.Version = value
}

function snapshotProjectGameVersion(project: ProjectVersionCarrier): { headerVersion: number; gameVersion: number } {
  return {
    headerVersion: Number(project.projectHeader.Version ?? 0) || 0,
    gameVersion: Number(project.projectConstant.gameVersion ?? 0) || 0,
  }
}

function restoreProjectGameVersion(
  project: ProjectVersionCarrier,
  snapshot: { headerVersion: number; gameVersion: number },
): void {
  project.projectHeader.Version = snapshot.headerVersion
  project.projectConstant.gameVersion = snapshot.gameVersion
}

function resolvePublishedVersion(response: Record<string, unknown>, fallback: string): string {
  const direct = String(response.version ?? response.ver ?? response.proj_ver ?? '').trim()
  if (direct) return direct
  const message = String(response.msg ?? '')
  const matched = message.match(/当前版本号[:：]\s*(\d+)/)
  return matched?.[1] || fallback
}

const PUBLISH_SUCCESS_COVER_TIP_BODY = '请至作者后台-作品管理-编辑作品页面上传作品封面'

/**
 * 解析发布成功文案中已存在的最大序号。
 * @param text 清洗后的发布成功文案。
 * @returns 最大序号；没有序号时返回 0。
 */
function resolveMaxPublishSuccessSequence(text: string): number {
  let max = 0
  const pattern = /(\d+)[、.．]/g
  let matched = pattern.exec(text)
  while (matched) {
    const value = Number.parseInt(matched[1], 10)
    if (Number.isFinite(value) && value > max) max = value
    matched = pattern.exec(text)
  }
  return max
}

/**
 * 按已有最大序号生成封面上传手动指引。
 * @param formatted 清洗后的发布成功文案。
 * @returns 带序号的封面指引文案。
 */
function buildPublishSuccessCoverTip(formatted: string): string {
  return `${resolveMaxPublishSuccessSequence(formatted) + 1}、${PUBLISH_SUCCESS_COVER_TIP_BODY}`
}

/**
 * 格式化最终发布接口返回的成功提示；封面未自动上传时追加手动指引。
 * @param response 最终发布接口原始返回。
 * @param coverUploadStatus 发布成功后封面上传结果。
 * @returns 展示给用户的发布成功文案。
 */
function formatPublishSuccessMessage(
  response: Record<string, unknown>,
  coverUploadStatus: PublishCoverUploadStatus = 'skipped',
): string {
  const message = String(response.msg ?? '发布成功').trim()
  const formatted = (message
    ? message
      .replace(/^(\s*\d+[、.．]\s*)当前版本号[:：]\s*\d+\s*[,，]?\s*/u, '$1')
      .replace(/^\s*当前版本号[:：]\s*\d+\s*[,，、]?\s*/u, '')
      .replace(/只能自己看到[。．.]/u, '只能自己看到')
      .trim()
    : '发布成功') || '发布成功'
  if (coverUploadStatus === 'success') return formatted
  if (formatted.includes(PUBLISH_SUCCESS_COVER_TIP_BODY)) return formatted
  return `${formatted}，${buildPublishSuccessCoverTip(formatted)}`
}

function collectPublishResultStats(project: MainProject): PublishResultStats {
  const stats: PublishResultStats = {
    picCount: 0,
    faceCount: 0,
    modCount: 0,
    bgmCount: 0,
    bgsCount: 0,
    seCount: 0,
    voiceCount: 0,
  }
  for (const map of project.storyTree.Maps) {
    for (const event of map.List as EventBase[]) {
      switch (event.Code) {
        case EventID.PicShow:
          stats.picCount += 1
          break
        case EventID.MoodShow:
        case EventID.GifPicShow:
          stats.modCount += 1
          break
        case EventID.StartBGM:
          stats.bgmCount += 1
          break
        case EventID.StartBGS:
          stats.bgsCount += 1
          break
        case EventID.StartSE:
          stats.seCount += 1
          break
        case EventID.StartVoice:
          stats.voiceCount += 1
          break
        case EventID.Text:
          if (String((event as EventBase & { Portrait?: string }).Portrait ?? '').trim()) {
            stats.faceCount += 1
          }
          break
        default:
          break
      }
    }
  }
  return stats
}

function getPublishAuthFailureMessage(data: Record<string, unknown>): string {
  const code = Number(data.code ?? (data.data as Record<string, unknown> | undefined)?.code ?? 0)
  if (code === 182) return '当前账号不是工程创建者，不能发布该作品'
  if (code === 28) return '工程不存在，不能发布'
  return String(data.msg ?? (data.data as Record<string, unknown> | undefined)?.msg ?? '当前账号不能发布该作品')
}

function getPublishAuthFailureCode(data: Record<string, unknown>): number {
  return Number(data.code ?? (data.data as Record<string, unknown> | undefined)?.code ?? 0)
}

async function isGuidInCurrentUserPublishedProjects(sessionId: string, guid: string): Promise<boolean> {
  const targetGuid = guid.trim().toLowerCase()
  if (!targetGuid) return false
  let page = 1
  let serverPageSize = 0
  while (page <= PUBLISHED_PROJECT_OWNER_CHECK_PAGE_LIMIT) {
    const result = await getPublishedCyGameListPage(sessionId, page)
    if (serverPageSize <= 0 && result.items.length > 0) {
      serverPageSize = result.items.length
    }
    if (!result.items.length) return false
    if (result.items.some((item) => item.guid.trim().toLowerCase() === targetGuid)) {
      return true
    }
    if (serverPageSize <= 0 || page * serverPageSize >= result.count) {
      return false
    }
    page += 1
  }
  return false
}

function getCurrentPublishAuth(): { sessionId: string; uid: number } {
  const auth = useAuthStore()
  if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
    throw new Error('请先登录橙光账号')
  }
  return {
    sessionId: auth.sessionId,
    uid: auth.uid,
  }
}

function getResponseMessage(data: Record<string, unknown>, fallback: string): string {
  const payload = (data.data as Record<string, unknown> | undefined) ?? data
  return String(payload.msg ?? data.msg ?? fallback).trim() || fallback
}

/*
* 同步作品名到工程与橙光云端，并刷新顶栏展示名。
*/
async function syncProjectNameBeforePublish(
  project: MainProject,
  context: PublishContext,
  projectName: string,
  hooks?: PublishFlowHooks,
) {
  const normalizedProjectName = projectName.trim()
  if (!normalizedProjectName) return
  project.storyTree.ProjectName = normalizedProjectName
  useWorkspaceStore().syncProjectDisplayName()
  markStep(hooks, 'project-name', 'running', '正在同步作品名')
  const { sessionId } = getCurrentPublishAuth()
  try {
    await updateProjectName(sessionId, context.guid, normalizedProjectName, hooks?.onRequest)
    markStep(hooks, 'project-name', 'done', '作品名已同步')
  } catch (error) {
    markStep(hooks, 'project-name', 'error', error instanceof Error ? error.message : '同步作品名失败')
    markRequest(
      hooks,
      'error',
      error instanceof Error ? `同步作品名称失败 · ${error.message}` : '同步作品名称失败',
    )
  }
}

async function pollQueue(
  upload: BrowserFileUpload,
  hooks: PublishFlowHooks | undefined,
  doneDetail: string,
  stepKey = 'runtime-queue',
): Promise<void> {
  let queueCount = 1
  while (queueCount > 0) {
    const response = await upload.getQueueInfo()
    const payload = JSON.parse(response) as { data?: { queue_count?: number } }
    queueCount = Number(payload.data?.queue_count ?? 0)
    markStep(hooks, stepKey, 'running', `服务器处理中，剩余 ${queueCount} 项`)
    if (queueCount > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 1500))
    }
  }
  markStep(hooks, stepKey, 'done', doneDetail)
}

async function publishProjectWithRetry(
  params: Record<string, string | number>,
  hooks?: PublishFlowHooks,
): Promise<Record<string, unknown>> {
  let lastMessage = '发布失败'
  for (let attempt = 1; attempt <= PUBLISH_GET_GAME_PATH_RETRY_COUNT; attempt += 1) {
    markStep(hooks, 'publish', 'running', `等待服务器确认，第 ${attempt} 次调用发布接口`)
    await waitPublishDelay(PUBLISH_GET_GAME_PATH_RETRY_DELAY_MS)
    try {
      const response = await publishProjectRequest(params, hooks?.onRequest)
      if (Number(response.ret ?? 0) === 1) {
        return response
      }
      lastMessage = String(response.msg ?? lastMessage)
    } catch (error) {
      lastMessage = error instanceof Error ? error.message : String(error)
    }
  }
  throw new Error(lastMessage)
}

export async function resolvePublishContext(hooks?: PublishFlowHooks): Promise<PublishContext> {
  const auth = useAuthStore()
  const workspace = useWorkspaceStore()
  const project = workspace.project
  if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
    throw new Error('请先登录橙光账号')
  }
  if (!project) {
    throw new Error('当前没有已打开工程')
  }
  if (workspace.openedOwnerUid && auth.uid !== workspace.openedOwnerUid) {
    throw new Error('当前登录账号与打开工程的账号不一致，请返回起始页后重新打开作品')
  }
  const guid = project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('工程缺少 guid，无法发布')
  }

  const localVersion = getLocalProjectVersion(project)
  let fallbackGindex: string | null = null
  let serverVersion = normalizePublishVersionNumber(
    await getPublishVersion(auth.sessionId, guid, hooks?.onRequest),
  )
  if (serverVersion <= 0) {
    const publishedGindex = await resolvePublishedGindexByGuid(auth.sessionId, guid)
    if (publishedGindex) {
      const latestPublished = resolveLatestPublishedVersion(await getPublishedProjectVersions(auth.sessionId, publishedGindex))
      if (latestPublished.version > 0) {
        serverVersion = latestPublished.version
        fallbackGindex = latestPublished.gindex
        markRequest(
          hooks,
          'done',
          `线上版本接口返回 0，已按已发布版本列表修正为 ${serverVersion}`,
        )
      }
    }
  }
  const isFirstPublish = serverVersion <= 0
  const publishVersion = String(isFirstPublish ? 1 : serverVersion + 1)
  let gindex: string | null = null
  if (!isFirstPublish) {
    try {
      gindex = await getPublishGindex(auth.sessionId, guid, String(serverVersion), hooks?.onRequest) ?? fallbackGindex
    } catch (error) {
      markRequest(
        hooks,
        'error',
        error instanceof Error ? `读取当前版本 gindex 失败 · ${error.message}` : '读取当前版本 gindex 失败',
      )
      gindex = fallbackGindex
    }
  }
  workspace.setPublishContext({ publishGindex: gindex })

  return {
    guid,
    localCurrentVersion: String(localVersion),
    serverCurrentVersion: String(serverVersion),
    publishVersion,
    gindex,
    isFirstPublish,
  }
}

export async function runPublishChecks(
  form: PublishFormModel,
  context: PublishContext,
  options?: PublishCheckOptions,
  hooks?: PublishFlowHooks,
): Promise<PublishCheckItem[]> {
  const workspace = useWorkspaceStore()
  const checks: PublishCheckItem[] = []
  const project = workspace.project
  if (!project) {
    return [makeCheck('project-missing', 'error', '工程未打开', '当前没有可发布的工程数据')]
  }

  if (!form.projectName.trim()) {
    checks.push(makeCheck('project-name', 'error', '缺少作品名', '请填写作品名称'))
  } else if (hasInvalidProjectNameChar(form.projectName)) {
    checks.push(makeCheck('project-name-invalid-char', 'error', '作品名包含禁止字符', PROJECT_NAME_INVALID_CHAR_MESSAGE))
  } else if (!isProjectNameLengthValid(form.projectName)) {
    checks.push(makeCheck('project-name-length', 'error', '作品名过长', PROJECT_NAME_LIMIT_MESSAGE))
  }
  const isFirstPublish = context.isFirstPublish
  const publishText = isFirstPublish ? form.synopsis.trim() : form.versionNote.trim()
  if (!publishText) {
    checks.push(
      makeCheck(
        isFirstPublish ? 'synopsis' : 'version-note',
        'warning',
        isFirstPublish ? '缺少作品简介' : '缺少更新日志',
        isFirstPublish ? '首次发布建议填写作品简介，主站会作为作品介绍展示' : '建议填写更新日志，便于发布记录与回溯',
      ),
    )
  }

  if (!options?.skipGateChecks && !isFirstPublish) {
    const { sessionId } = getCurrentPublishAuth()
    const authRes = await checkProjectPublishAuth(sessionId, context.guid, hooks?.onRequest)
    if (Number(authRes.ret ?? 1) !== 1) {
      if (
        getPublishAuthFailureCode(authRes) === 28
        && await isGuidInCurrentUserPublishedProjects(sessionId, context.guid)
      ) {
        checks.push(makeCheck('publish-auth-owner-fallback', 'info', '发布权限已确认', '已在当前账号的已发布作品列表中确认该作品，继续发布'))
      } else {
        checks.push(makeCheck('publish-auth', 'error', '无发布权限', getPublishAuthFailureMessage(authRes)))
      }
    }
  }

  let shouldRequireTag = false
  if (!options?.skipGateChecks) {
    shouldRequireTag = isFirstPublish
  }
  if (!options?.skipGateChecks && !shouldRequireTag) {
    const { sessionId, uid } = getCurrentPublishAuth()
    const tagRes = await checkIfNeedTag({
      uid,
      sessionId,
      guid: context.guid,
    }, hooks?.onRequest)
    shouldRequireTag = isTagMissing(tagRes)
  }
  if (shouldRequireTag) {
    if (options?.tagSelectionSubmitted) {
      checks.push(makeCheck('need-tag-sync', 'info', '标签已提交', '当前会话内已完成标签提交，请重新发布前再执行一次检查确认线上状态'))
    } else if (options?.stagedTagSelection) {
      checks.push(makeCheck('need-tag-staged', 'info', '标签已准备', '当前会话内已完成标签选择；若本次是首次发布，发布成功后会自动补提标签'))
    } else {
      checks.push(
        makeCheck(
          'need-tag',
          'error',
          '缺少作品标签',
          '首次发布前需先设置标签。请完成标签选择后再继续发布。',
        ),
      )
    }
  }

  try {
    const { sessionId } = getCurrentPublishAuth()
    const [nameRes, publishTextRes] = await Promise.all([
      form.projectName.trim()
        ? checkBlackWords({ sessionId, type: 6, content: form.projectName.trim() }, hooks?.onRequest)
        : Promise.resolve({ ret: 1 } as Record<string, unknown>),
      publishText
        ? checkBlackWords({ sessionId, type: isFirstPublish ? 5 : 13, content: publishText }, hooks?.onRequest)
        : Promise.resolve({ ret: 1 } as Record<string, unknown>),
    ])
    if (Number(nameRes.ret ?? 1) !== 1) {
      checks.push(makeCheck('name-blackwords', 'error', '作品名包含非法词条', getResponseMessage(nameRes, '请修改作品名后再发布')))
    }
    if (Number(publishTextRes.ret ?? 1) !== 1) {
      checks.push(
        makeCheck(
          isFirstPublish ? 'synopsis-blackwords' : 'version-note-blackwords',
          'error',
          isFirstPublish ? '作品简介包含非法词条' : '更新日志包含非法词条',
          getResponseMessage(publishTextRes, isFirstPublish ? '请修改作品简介后再发布' : '请修改更新日志后再发布'),
        ),
      )
    }
  } catch (error) {
    checks.push(
      makeCheck(
        'blackwords-network',
        'error',
        '敏感词检查未完成',
        error instanceof Error ? error.message : '敏感词检查失败，请稍后重试',
      ),
    )
  }

  return checks
}

export async function runPublishGateChecks(
  context: PublishContext,
  options?: PublishCheckOptions,
  hooks?: PublishFlowHooks,
): Promise<PublishCheckItem[]> {
  const checks: PublishCheckItem[] = []
  if (!context.isFirstPublish) {
    const { sessionId } = getCurrentPublishAuth()
    const authRes = await checkProjectPublishAuth(sessionId, context.guid, hooks?.onRequest)
    if (Number(authRes.ret ?? 1) !== 1) {
      if (
        getPublishAuthFailureCode(authRes) === 28
        && await isGuidInCurrentUserPublishedProjects(sessionId, context.guid)
      ) {
        checks.push(makeCheck('publish-auth-owner-fallback', 'info', '发布权限已确认', '已在当前账号的已发布作品列表中确认该作品，继续发布'))
      } else {
        checks.push(makeCheck('publish-auth', 'error', '无发布权限', getPublishAuthFailureMessage(authRes)))
      }
    }
  }

  let shouldRequireTag = context.isFirstPublish
  if (!shouldRequireTag) {
    const { sessionId, uid } = getCurrentPublishAuth()
    const tagRes = await checkIfNeedTag({
      uid,
      sessionId,
      guid: context.guid,
    }, hooks?.onRequest)
    shouldRequireTag = isTagMissing(tagRes)
  }
  if (shouldRequireTag) {
    if (options?.tagSelectionSubmitted) {
      checks.push(makeCheck('need-tag-sync', 'info', '标签已提交', '当前会话内已完成标签提交，请重新发布前再执行一次检查确认线上状态'))
    } else if (options?.stagedTagSelection) {
      checks.push(makeCheck('need-tag-staged', 'info', '标签已准备', '当前会话内已完成标签选择；若本次是首次发布，发布成功后会自动补提标签'))
    } else {
      checks.push(
        makeCheck(
          'need-tag',
          'error',
          '缺少作品标签',
          '首次发布前需先设置标签。请完成标签选择后再继续发布。',
        ),
      )
    }
  }
  return checks
}

function buildUploadTasks(
  artifacts: PublishArtifacts,
  context: PublishContext,
  mapContentMd5: string,
  hooks?: PublishFlowHooks,
): UploadTaskFile[] {
  const tasks: UploadTaskFile[] = []
  tasks.push({
    file: createUploadFile(artifacts.mapBin),
    fileName: artifacts.mapBin.uploadPath,
    op: '3',
    shared: '0',
    contentMd5: mapContentMd5,
  })

  const uploadableFiles = [...artifacts.runtimeFiles, ...artifacts.fontFiles]
  for (const file of uploadableFiles) {
    const uploadFile = createUploadFile(file)
    tasks.push({
      file: uploadFile,
      fileName: file.uploadPath,
      op: '3',
      shared: '1',
    })
    tasks.push({
      file: uploadFile,
      fileName: file.uploadPath,
      op: '0',
      shared: '1',
    })
  }
  markStep(hooks, 'runtime-plan', 'done', `准备上传 ${tasks.length} 个任务（运行时文件 ${uploadableFiles.length} 个，发布清单 1 个）`)
  return tasks
}

async function uploadPublishedProjectSnapshot(
  artifacts: PublishArtifacts,
  context: PublishContext,
  hooks?: PublishFlowHooks,
): Promise<void> {
  const objectName = `${context.guid}/${artifacts.editorProjectBin.uploadPath}`
  const { sessionId } = getCurrentPublishAuth()
  const projectUpload = new BrowserFileUpload(
    {
      guid: context.guid,
      version: APP_VERSION,
      proj_ver: context.publishVersion,
      sessionid: sessionId,
    },
    [
      {
        file: createUploadFile(artifacts.editorProjectBin),
        fileName: objectName,
        op: '0',
        shared: '0',
        contentMd5: artifacts.editorProjectBin.md5,
      },
    ],
    (fileName, percent, status) => {
      hooks?.onUploadProgress?.(fileName, percent, status)
    },
    (status, detail) => {
      markRequest(hooks, status, detail)
    },
    { skipPreCheck: true },
  )
  await projectUpload.uploadFile()
  await pollQueue(projectUpload, hooks, '工程快照处理完成，正在确认', 'snapshot-queue')
  await verifyPublishedProjectSnapshot(artifacts, context, objectName, hooks)
}

async function verifyPublishedProjectSnapshot(
  artifacts: PublishArtifacts,
  context: PublishContext,
  objectName: string,
  hooks?: PublishFlowHooks,
): Promise<void> {
  markStep(hooks, 'snapshot-verify', 'running', '正在确认工程快照')
  markRequest(hooks, 'running', '工程快照确认 · start')
  let sessionId = ''
  try {
    sessionId = getCurrentPublishAuth().sessionId
  } catch (error) {
    const message = error instanceof Error ? error.message : '登录态校验失败'
    markStep(hooks, 'snapshot-verify', 'error', `登录态异常：${message}`)
    markRequest(hooks, 'error', `工程快照确认失败 · 登录态异常 · ${message}`)
    throw error
  }
  let bytes: Uint8Array
  try {
    bytes = await downloadPublishedProjectFile({
      guid: context.guid,
      sessionId,
      toolVersion: context.publishVersion,
      objectName,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : '工程快照下载失败'
    markRequest(
      hooks,
      'error',
      `工程快照确认失败 · 下载失败 · version=${context.publishVersion} · object=${objectName} · ${message}`,
    )
    markStep(hooks, 'snapshot-verify', 'error', message)
    throw error
  }
  const md5 = BrowserFileUtils.getMd5HashFromUint8Array(bytes)
  if (md5 !== artifacts.editorProjectBin.md5) {
    const message = `工程快照确认失败：remote=${md5}，local=${artifacts.editorProjectBin.md5}，version=${context.publishVersion}，object=${objectName}`
    markRequest(
      hooks,
      'error',
      message,
    )
    markStep(hooks, 'snapshot-verify', 'error', message)
    throw new Error(message)
  }
  markRequest(hooks, 'done', '工程快照已确认')
  markStep(hooks, 'snapshot-verify', 'done', '工程快照已确认')
}

async function verifyMapBinContentMd5(artifacts: PublishArtifacts): Promise<string> {
  const uploadFile = createUploadFile(artifacts.mapBin)
  const fileMd5 = await BrowserFileUtils.getMd5HashFromFile(uploadFile)
  if (fileMd5 !== artifacts.mapBin.md5) {
    throw new Error(
      `发布清单 MD5 校验失败：artifact=${artifacts.mapBin.md5}，uploadFile=${fileMd5}，size=${artifacts.mapBin.size}`,
    )
  }
  return fileMd5
}

async function confirmMapBinPutSign(
  artifacts: PublishArtifacts,
  context: PublishContext,
  mapContentMd5: string,
  hooks?: PublishFlowHooks,
): Promise<void> {
  markStep(hooks, 'map-confirm', 'running', '正在确认发布清单')
  markRequest(hooks, 'running', '发布清单确认 · start')
  const { sessionId } = getCurrentPublishAuth()
  const response = await confirmPublishFilePutSign({
    guid: context.guid,
    sessionId,
    version: APP_VERSION,
    publishVersion: context.publishVersion,
    fileName: MAP_BIN_PUTSIGN_FILE_NAME,
    contentMd5: mapContentMd5,
    shared: '0',
  }, hooks?.onRequest)
  if (Number(response.ret ?? 0) !== 1) {
    throw new Error(String(response.msg ?? '发布清单确认失败'))
  }
  markRequest(hooks, 'done', '发布清单确认完成')
  markStep(hooks, 'map-confirm', 'done', '发布清单已确认')
}

function resolvePublishResponseUrl(response: Record<string, unknown>): string {
  return String(response.game_url ?? response.url ?? '').trim()
}

function resolveGindexFromPublishResponse(response: Record<string, unknown>): string | null {
  const direct = String(response.gindex ?? '').trim()
  if (direct) return direct
  const urlText = resolvePublishResponseUrl(response)
  if (!urlText) return null
  try {
    const url = new URL(urlText, window.location.origin)
    return url.pathname.split('/').filter(Boolean).pop() ?? null
  } catch {
    return urlText.split(/[?#]/)[0]?.split('/').filter(Boolean).pop() ?? null
  }
}

/*
* 执行发布
*/
export async function executePublish(
  form: PublishFormModel,
  context: PublishContext,
  hooks?: PublishFlowHooks,
  options?: {
    pendingTagSelection?: PublishRawTagList | null
  },
): Promise<PublishResult> {
  const workspace = useWorkspaceStore()
  const project = workspace.project as MainProject | null
  if (!project) {
    throw new Error('当前没有已打开工程')
  }

  const versionSnapshot = snapshotProjectGameVersion(project)
  let publishAccepted = false

  await syncProjectNameBeforePublish(project, context, form.projectName, hooks)

  markStep(hooks, 'artifacts', 'running', '发布前生成位图字体')
  await workspace.generateBitmapFont()
  writeProjectGameVersion(project, context.publishVersion)

  let artifacts!: PublishArtifacts
  let publishRes!: Record<string, unknown>
  let finalPublishVersion = context.publishVersion
  let finalGindex: string | null = null
  const isFirstPublish = context.isFirstPublish
  const publishText = isFirstPublish ? form.synopsis.trim() : form.versionNote.trim()
  let publishAuth = getCurrentPublishAuth()

  try {
    markStep(hooks, 'artifacts', 'running', '生成发布产物')
    artifacts = await buildPublishArtifacts(project, workspace.virtualResourceRoots, {
      publishVersion: context.publishVersion,
    })
    markStep(
      hooks,
      'artifacts',
      'done',
      `已生成 ${artifacts.runtimeFiles.length} 个运行时 bin 和 ${artifacts.fontFiles.length} 个字体文件`,
    )
    markRequest(
      hooks,
      'running',
      `发布清单已生成 · size=${artifacts.mapBin.size} · publishContentSize=${artifacts.publishContentSize}`,
    )
    const mapContentMd5 = await verifyMapBinContentMd5(artifacts)
    markRequest(hooks, 'done', '发布清单校验完成')

    markStep(hooks, 'snapshot-upload', 'running', '正在上传工程快照')
    await uploadPublishedProjectSnapshot(artifacts, context, hooks)
    markStep(hooks, 'snapshot-upload', 'done', '工程快照已上传')
    markStep(hooks, 'runtime-upload', 'running', '工程快照已确认，开始上传运行文件')

    publishAuth = getCurrentPublishAuth()
    const upload = new BrowserFileUpload(
      {
        guid: context.guid,
        version: APP_VERSION,
        proj_ver: context.publishVersion,
        sessionid: publishAuth.sessionId,
      },
      buildUploadTasks(artifacts, context, mapContentMd5, hooks),
      (fileName, percent, status) => {
        hooks?.onUploadProgress?.(fileName, percent, status)
      },
      (status, detail) => {
        markRequest(hooks, status, detail)
      },
    )

    markStep(hooks, 'runtime-upload', 'running', '开始上传运行文件')
    await upload.uploadFile()
    markStep(hooks, 'runtime-upload', 'done', '运行文件上传任务已提交')

    markStep(hooks, 'runtime-queue', 'running', '上传完成，正在等待服务器处理')
    await pollQueue(upload, hooks, '运行文件处理完成，正在提交发布')
    await confirmMapBinPutSign(artifacts, context, mapContentMd5, hooks)

    /**
     * 提交发布的参数
     */
    const publishParams: Record<string, string | number> = {
      guid: context.guid,
      width: project.screenHash.width,
      height: project.screenHash.height,
      word: artifacts.totalWordCount,
      size: artifacts.publishContentSize,
      sessionid: getCurrentPublishAuth().sessionId,
      channel_id: 0,
      client_type: 0,
      content_md5: mapContentMd5,
      pay_type: form.payType,
      hp_number: form.hpNumber,
      is_fixed_time_release: 0,
      have_update_log: publishText ? 1 : 0,
      flower_unlock: form.flowerUnlock,
      pmode: 0,
      proj_name: form.projectName.trim(),
      ver: context.publishVersion,
      fixed_time: formatPublishFixedTime(),
      target: 2,
      version: APP_VERSION,
      tool_name: APP_NAME,
      dubbing_flag: 0,
      chengxi_flag: 0,
    }

    publishRes = await publishProjectWithRetry(publishParams, hooks)
    publishAccepted = true
    markStep(hooks, 'publish', 'done', String(publishRes.msg ?? '发布成功'))
    finalPublishVersion = resolvePublishedVersion(publishRes, context.publishVersion)
    finalGindex = resolveGindexFromPublishResponse(publishRes)
    const publishUrl = resolvePublishResponseUrl(publishRes)
    const resultStats = collectPublishResultStats(project)
    publishAuth = getCurrentPublishAuth()
    await submitPublishResult(
      {
        info: BrowserFileUtils.stringtoHex(JSON.stringify({ url: publishUrl })),
        guid: context.guid,
        sessionid: publishAuth.sessionId,
        version: APP_VERSION,
        tool_name: APP_NAME,
        channel_id: 0,
        gindex: finalGindex ?? '',
        cur_version: finalPublishVersion,
        content_md5: mapContentMd5,
        target: 2,
      },
      [
        finalPublishVersion,
        artifacts.publishContentSize,
        form.projectName.trim(),
        project.dataVer,
        publishUrl,
        '',
        APP_VERSION,
        artifacts.totalWordCount,
        '',
        project.systemData.CG.CGs.length,
        project.systemData.BGM.BGMs.length,
        resultStats.picCount,
        resultStats.faceCount,
        resultStats.modCount,
        resultStats.bgmCount,
        resultStats.bgsCount,
        resultStats.seCount,
        resultStats.voiceCount,
      ],
      hooks?.onRequest,
    )
  } catch (error) {
    if (!publishAccepted) {
      restoreProjectGameVersion(project, versionSnapshot)
    }
    throw error
  }

  if (!finalGindex) {
    try {
      finalGindex = await getPublishGindex(getCurrentPublishAuth().sessionId, context.guid, finalPublishVersion, hooks?.onRequest)
    } catch (error) {
      markRequest(
        hooks,
        'error',
        error instanceof Error ? `读取新版本 gindex 失败 · ${error.message}` : '读取新版本 gindex 失败',
      )
    }
  }

  if (publishText && finalGindex) {
    try {
      markStep(hooks, 'update-log', 'running', isFirstPublish ? '写入作品简介' : '写入更新日志')
      await addUpdateLog(getCurrentPublishAuth().sessionId, {
        gindex: finalGindex,
        logIntro: publishText,
        authorYesSay: '',
      }, hooks?.onRequest)
      markStep(hooks, 'update-log', 'done', isFirstPublish ? '作品简介已写入' : '更新日志已写入')
    } catch (error) {
      markStep(hooks, 'update-log', 'error', error instanceof Error ? error.message : (isFirstPublish ? '作品简介写入失败' : '更新日志写入失败'))
    }
  } else {
    markStep(hooks, 'update-log', 'done', isFirstPublish ? '未填写作品简介，已跳过' : '未填写更新日志，已跳过')
  }

  workspace.setPublishContext({
    publishGindex: finalGindex ?? '',
  })
  writeProjectGameVersion(project, finalPublishVersion)
  try {
    markStep(hooks, 'post-save', 'running', '同步轻量版云端草稿')
    await projectOssSaveService.save({
      project,
      virtualResourceRoots: workspace.virtualResourceRoots,
      uid: getCurrentPublishAuth().uid,
      publishGindex: finalGindex,
      projectBinBytes: artifacts.editorProjectBin.bytes,
      onProgress: (progress) => {
        const status = progress.stage === 'prepare' ? '准备同步轻量版云端草稿' : '同步轻量版云端草稿'
        markStep(hooks, 'post-save', 'running', status)
      },
    })
    markStep(hooks, 'post-save', 'done', '轻量版云端草稿已同步')
  } catch (error) {
    markStep(
      hooks,
      'post-save',
      'error',
      error instanceof Error ? error.message : '同步轻量版云端草稿失败',
    )
    markRequest(
      hooks,
      'error',
      error instanceof Error ? `同步轻量版云端草稿失败 · ${error.message}` : '同步轻量版云端草稿失败',
    )
  }

  if (!form.canSyncGameAttrs) {
    markStep(hooks, 'game-attrs', 'done', '线上作品已完结，投稿详情同步跳过')
  } else if (finalGindex) {
    try {
      markStep(hooks, 'game-attrs', 'running', '同步投稿详情')
      const copyrightType =
        form.copyrightAction === 'get_partner_list'
          ? 2
          : form.copyrightAction === 'star_copyrigt_company_list'
            ? 3
            : 1
      publishAuth = getCurrentPublishAuth()
      await addGameAttrs({
        uid: publishAuth.uid,
        sessionid: publishAuth.sessionId,
        gindex: finalGindex,
        complete_status: context.isFirstPublish ? 0 : (form.isFinished ? 1 : 0),
        activity_tid: form.activityTid > 0 ? form.activityTid : 0,
        star_id: form.copyrightAction === 'star_copyrigt_company_list' ? Math.max(0, form.copyrightWorkId) : 0,
        book_id: form.copyrightAction === 'get_partner_list' ? Math.max(0, form.copyrightWorkId) : 0,
        screen_type: form.screenType,
        type: copyrightType,
      }, hooks?.onRequest)
      markStep(hooks, 'game-attrs', 'done', '投稿详情已同步')
    } catch (error) {
      markStep(hooks, 'game-attrs', 'error', error instanceof Error ? error.message : '投稿详情同步失败')
    }
  } else {
    markStep(hooks, 'game-attrs', 'done', '未拿到 gindex，投稿详情跳过')
  }
  let tagSubmitted = false
  if (options?.pendingTagSelection) {
    if (!finalGindex) {
      markStep(hooks, 'tag-submit', 'error', '发布成功，但未拿到 gindex，无法自动提交标签')
    } else {
      try {
        markStep(hooks, 'tag-submit', 'running', '提交作品标签')
        publishAuth = getCurrentPublishAuth()
        await submitGameTags({
          uid: publishAuth.uid,
          sessionId: publishAuth.sessionId,
          gindex: finalGindex,
          guid: context.guid,
          screenType: form.screenType,
          payType: form.payType,
          tagList: options.pendingTagSelection,
        }, hooks?.onRequest)
        tagSubmitted = true
        markStep(hooks, 'tag-submit', 'done', '作品标签已提交')
      } catch (error) {
        markStep(hooks, 'tag-submit', 'error', error instanceof Error ? error.message : '作品标签提交失败')
      }
    }
  } else {
    markStep(hooks, 'tag-submit', 'done', '当前无待提交标签，已跳过')
  }

  let coverUploadStatus: PublishCoverUploadStatus = 'skipped'
  try {
    markStep(hooks, 'cover-upload', 'running', '正在上传作品封面')
    publishAuth = getCurrentPublishAuth()
    const coverResult = await uploadPublishedCover({
      project,
      graphicRoot: workspace.virtualResourceRoots.graphic,
      resourcePreviewOverrides: workspace.resourcePreviewOverrides,
      guid: context.guid,
      publishVersion: finalPublishVersion,
      sessionId: publishAuth.sessionId,
      onTrace: hooks?.onRequest,
    })
    coverUploadStatus = coverResult.status
    markStep(
      hooks,
      'cover-upload',
      coverResult.status === 'failed' ? 'error' : 'done',
      coverResult.detail,
    )
  } catch (error) {
    coverUploadStatus = 'failed'
    markStep(
      hooks,
      'cover-upload',
      'error',
      error instanceof Error ? error.message : '作品封面上传失败',
    )
  }

  return {
    gameUrl: finalGindex ? buildPublishedGamePageUrl(finalGindex) ?? undefined : undefined,
    message: formatPublishSuccessMessage(publishRes, coverUploadStatus),
    publishVersion: finalPublishVersion,
    gindex: finalGindex,
    tagSubmitted,
  }
}
